import { useState, useCallback, useRef, useEffect } from 'react';
import { debounce } from '../../../../../utils/debounce';

// Get current day of week (0=Saturday in Jalali calendar)
function getCurrentJalaliDay() {
    const jsDay = new Date().getDay(); // 0=Sunday, 1=Monday, ..., 6=Saturday
    // Convert to Jalali: Saturday=0, Sunday=1, Monday=2, ..., Friday=6
    const jalaliDay = (jsDay + 1) % 7;
    return jalaliDay;
}

export function useStudentCardFilters(onFiltersChange) {
    const [filters, setFilters] = useState({
        courses: [],
        student: '',
        remained_sessions_lte: '',
        days: [getCurrentJalaliDay()],
        no_attendance_today: false,
        attendance_done_today: false,
    });
    
    const [isTyping, setIsTyping] = useState(false);
    const filtersRef = useRef(filters);

    // Update ref when filters change
    useEffect(() => {
        filtersRef.current = filters;
    }, [filters]);

    const handleFilterChange = useCallback((field, value) => {
        // Set typing state for text inputs
        if (field === 'student') {
            setIsTyping(true);
        }
        
        // no_attendance_today and attendance_done_today are mutually exclusive:
        // turning one on forces the other off.
        if (field === 'no_attendance_today' && value) {
            setFilters(prev => ({ ...prev, no_attendance_today: true, attendance_done_today: false }));
            return;
        }
        if (field === 'attendance_done_today' && value) {
            setFilters(prev => ({ ...prev, attendance_done_today: true, no_attendance_today: false }));
            return;
        }

        setFilters(prev => ({ ...prev, [field]: value }));
    }, []);

    const handleDayChange = useCallback((dayValue) => {
        setFilters(prev => {
            const currentDays = prev.days;
            if (currentDays.includes(dayValue)) {
                // Remove day if already selected
                return { ...prev, days: currentDays.filter(d => d !== dayValue) };
            } else {
                // Add day if not selected
                return { ...prev, days: [...currentDays, dayValue] };
            }
        });
    }, []);

    const handleCourseChange = useCallback((courseId) => {
        setFilters(prev => {
            const currentCourses = prev.courses;
            if (currentCourses.includes(courseId)) {
                // Remove course if already selected
                return { ...prev, courses: currentCourses.filter(c => c !== courseId) };
            } else {
                // Add course if not selected
                return { ...prev, courses: [...currentCourses, courseId] };
            }
        });
    }, []);

    const hasActiveFilters = useCallback(() => {
        const defaultDay = getCurrentJalaliDay();
        return (
            filters.courses.length > 0 ||
            filters.student !== '' ||
            filters.remained_sessions_lte !== '' ||
            filters.days.length !== 1 || filters.days[0] !== defaultDay ||
            filters.no_attendance_today !== false ||
            filters.attendance_done_today !== false
        );
    }, [filters]);

    const handleClearFilters = useCallback(() => {
        const defaultFilters = {
            courses: [],
            student: '',
            remained_sessions_lte: '',
            days: [getCurrentJalaliDay()],
            no_attendance_today: false,
            attendance_done_today: false,
        };
        setIsTyping(false);
        setFilters(defaultFilters);
    }, []);

    // Build API params
    const buildParams = useCallback(() => {
        const params = {};
        if (filtersRef.current.courses && filtersRef.current.courses.length > 0) {
            params.course_ids = filtersRef.current.courses.join(',');
        }
        if (filtersRef.current.student) params.student = filtersRef.current.student;
        if (filtersRef.current.remained_sessions_lte) params.remained_sessions_lte = filtersRef.current.remained_sessions_lte;
        if (filtersRef.current.days && filtersRef.current.days.length > 0) {
            // If all days are selected, don't send day parameter (show all)
            if (filtersRef.current.days.length !== 7) {
                params.day = filtersRef.current.days.join(',');
            }
        }
        if (filtersRef.current.no_attendance_today) params.no_attendance_today = 'true';
        if (filtersRef.current.attendance_done_today) params.attendance_done_today = 'true';
        return params;
    }, []);

    return {
        filters,
        isTyping,
        setIsTyping,
        handleFilterChange,
        handleDayChange,
        handleCourseChange,
        hasActiveFilters,
        handleClearFilters,
        buildParams
    };
}

